import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, User, CheckCircle, ChevronDown, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@ui/button';
import { Badge } from '@ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card';
import { Progress } from '@ui/progress';
import { format } from 'date-fns';
import { BaseLayout } from '@/components/layout';
import { useAreaTagsByProject } from '@/features/area-tags/hooks/useAreaTags';
import { usePunchListDetail } from '../hooks/punch-list-hooks';
import {
  computeCompletionPercent,
  getItemCountsForPunchlist,
  derivePunchlistStatus,
  formatFieldTypeBadgeColor,
} from '../utils/punch-list-utils';
import type { PunchlistItem as PunchlistItemType } from '../types';
import PunchListActivityFeed from './PunchListActivityFeed';

const PunchListDetail = () => {
  const { projectId, punchlistId } = useParams<{ projectId: string; punchlistId: string }>();
  const navigate = useNavigate();
  const { data: punchlist, isLoading, error } = usePunchListDetail(punchlistId ?? '');
  const { data: areaTags = [] } = useAreaTagsByProject(projectId ?? '', { enabled: !!projectId });
  const backToPunchListsPath = projectId ? `/app/property/${projectId}/punch-lists` : '/app/projects/all';

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  if (isLoading) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="p-6">
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">Failed to load punch list. Please try again later.</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (!punchlist) {
    return (
      <BaseLayout>
        <div className="p-6">
          <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">Punch list not found.</p>
          </div>
          <Button variant="outline" className="mt-4" onClick={() => navigate(backToPunchListsPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Punch Lists
          </Button>
        </div>
      </BaseLayout>
    );
  }

  const status = derivePunchlistStatus(punchlist);
  const completionPercent = computeCompletionPercent(punchlist);
  const { total, completed } = getItemCountsForPunchlist(punchlist);
  const areaLabel =
    punchlist.area == null
      ? 'General'
      : areaTags.find((areaTag) => areaTag.id === punchlist.area)?.name ||
        `Area #${punchlist.area}`;

  return (
    <BaseLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate(backToPunchListsPath)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{punchlist.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant={status === 'completed' ? 'default' : 'secondary'}
                  className={
                    status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                  }
                >
                  {status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In Progress' : 'Empty'}
                </Badge>
                <span className="text-sm text-muted-foreground">{completionPercent}% complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Completion Banner */}
        <Card
          className={
            status === 'completed'
              ? 'border-green-200 bg-green-50'
              : 'border-blue-200 bg-blue-50'
          }
        >
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle
                  className={`h-5 w-5 ${status === 'completed' ? 'text-green-600' : 'text-blue-600'}`}
                />
                <span
                  className={`text-sm font-medium ${status === 'completed' ? 'text-green-800' : 'text-blue-800'}`}
                >
                  {status === 'completed'
                    ? `Completed on ${format(new Date(punchlist.updated_at), 'PPP')}`
                    : `In Progress — ${completionPercent}% complete (${completed}/${total} items)`}
                </span>
              </div>
            </div>
            <Progress
              value={completionPercent}
              className={`h-2 ${status === 'completed' ? '[&>div]:bg-green-600' : '[&>div]:bg-blue-600'}`}
            />
          </CardContent>
        </Card>

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div>
                    <dt className="font-medium text-muted-foreground">Title</dt>
                    <dd className="mt-0.5">{punchlist.title}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Type</dt>
                    <dd className="mt-0.5">
                      <Badge variant="outline">
                        {punchlist.is_template ? 'Template' : 'Instance'}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Area</dt>
                    <dd className="mt-0.5">{areaLabel}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="font-medium text-muted-foreground">Description</dt>
                    <dd className="mt-0.5">{punchlist.description || '—'}</dd>
                  </div>
                  {punchlist.template_source && (
                    <div>
                      <dt className="font-medium text-muted-foreground">Template Source</dt>
                      <dd className="mt-0.5 truncate">{punchlist.template_source}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <User className="h-3.5 w-3.5" /> Created By
                    </dt>
                    <dd className="mt-0.5">{punchlist.created_by}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created At
                    </dt>
                    <dd className="mt-0.5">{format(new Date(punchlist.created_at), 'PPp')}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Updated At
                    </dt>
                    <dd className="mt-0.5">{format(new Date(punchlist.updated_at), 'PPp')}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Sections */}
            {punchlist.sections
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((section) => {
                const sectionCompleted = section.items.filter((i) => i.is_completed).length;
                const sectionTotal = section.items.length;
                const isCollapsed = collapsedSections[section.id] ?? false;

                return (
                  <Card key={section.id}>
                    <CardHeader
                      className="cursor-pointer select-none"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4 shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0" />
                          )}
                          {section.title}
                        </CardTitle>
                        <span className="text-sm text-muted-foreground whitespace-nowrap ml-3">
                          {sectionCompleted}/{sectionTotal} completed
                        </span>
                      </div>
                    </CardHeader>

                    {!isCollapsed && (
                      <CardContent className="pt-0">
                        <div className="divide-y">
                          {section.items
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((item) => (
                              <SectionItemRow key={item.id} item={item} />
                            ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
          </div>

          {/* Right column */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <PunchListActivityFeed punchlistId={punchlistId!} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

function SectionItemRow({ item }: { item: PunchlistItemType }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="mt-0.5">
        {item.is_completed ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{item.name}</span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${formatFieldTypeBadgeColor(item.field_type)}`}
          >
            {item.field_type}
          </span>
          {item.is_required && (
            <span className="text-red-500 text-sm font-bold" title="Required">*</span>
          )}
        </div>
        <p className={`text-xs mt-0.5 ${item.response?.display_value ? 'text-muted-foreground' : 'text-gray-400 italic'}`}>
          {item.response?.display_value || 'No response'}
        </p>
      </div>
    </div>
  );
}

export default PunchListDetail;
